import { Loader2, Search, Trash2, X } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import investorStrategyService, {
  isAdminEditableRecord,
} from "../../services/investorStrategyService";
import type { InvestorStrategyRecord } from "../../services/investorStrategyService";
import type { AdminProductSearchItem, ProductSearchItem } from "../../types/productV3";
import { formatWeight, roundToDecimals } from "../../utils/numberUtils";
import { BASE_STRATEGY_ALLOCATION_LIMIT } from "../../utils/strategyAllocationLimits";
import {
  isValidNlv,
  weightToWholeDollars,
  wholeDollarsToWeight,
} from "../../utils/strategyDollarInputMode";
import { getStrategyDisplayName, ProductLookupEntry, ProductLookupKey } from "../../utils/strategyUtils";
import ScrollableTableWrapper from "../ScrollableTableWrapper";

type EditableEntityType = "PORTFOLIO" | "BASKET" | "MANAGED_STRATEGY";

interface EditEntry {
  entityType: EditableEntityType;
  productId: number;
  weight: number;
  position: "LONG";
  displayName: string;
}

/**
 * One row the add-search can offer. Portfolios/baskets come from the admin
 * catalogue; managed strategies come from the all-types admin search, which is
 * the only route that returns them (searchAdminProducts strips them by design).
 */
type AddableProduct = Pick<
  AdminProductSearchItem,
  "productType" | "productId" | "title" | "providerName"
>;

interface AdminStrategyEditorProps {
  userId: string;
  /**
   * Every held record, managed strategies included. This editor renders all of
   * them, so a record omitted from a save here is a deliberate removal.
   */
  records: InvestorStrategyRecord[];
  productItems: ProductSearchItem[];
  /**
   * Managed strategies offered by the add-search. Optional: omit it and the
   * editor still edits and removes managed rows the account already holds, it
   * just cannot add a new one.
   */
  managedItems?: AddableProduct[];
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
  nlv: number | null;
  allocationLimit?: number;
  isLoadingNlv?: boolean;
  onSave: () => void;
  onCancel: () => void;
}

const AdminStrategyEditor: React.FC<AdminStrategyEditorProps> = ({
  userId,
  records,
  productItems,
  managedItems = [],
  productLookup,
  nlv,
  allocationLimit = BASE_STRATEGY_ALLOCATION_LIMIT,
  isLoadingNlv = false,
  onSave,
  onCancel,
}) => {
  const [editEntries, setEditEntries] = useState<EditEntry[]>(() =>
    records.map((s) => ({
      entityType: s.entityType,
      productId: s.productId,
      weight: parseFloat(s.weight) || 0,
      position: "LONG",
      displayName: getStrategyDisplayName(s, productLookup),
    }))
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [dollarInputs, setDollarInputs] = useState<Record<string, string>>({});
  const canEditDollarAllocation = !isLoadingNlv && isValidNlv(nlv);

  const totalWeight = useMemo(
    () => editEntries.reduce((sum, e) => sum + e.weight, 0),
    [editEntries]
  );

  const hasChanges = useMemo(() => {
    if (editEntries.length !== records.length) return true;
    return editEntries.some((e) => {
      const orig = records.find(
        (s) => s.entityType === e.entityType && s.productId === e.productId
      );
      if (!orig) return true;
      return Math.abs(e.weight - (parseFloat(orig.weight) || 0)) > 0.001;
    });
  }, [editEntries, records]);

  // Every held record is rendered above, so the whole account budget is
  // editable here and the "Total X% / Y%" readout describes the whole of what
  // the save writes.
  const editableLimit = allocationLimit;
  const isOverAllocationLimit = totalWeight > editableLimit;

  // The investor's own allocations screen goes fail-closed read-only when it
  // finds more than one managed row (sizing and rematching are singleton
  // operations there). An admin save must not be able to put a user into that
  // state, so block it here with the same rule.
  const managedEntryCount = useMemo(
    () =>
      editEntries.filter(
        (e) => e.entityType === "MANAGED_STRATEGY" && e.weight > 0
      ).length,
    [editEntries]
  );
  const hasMultipleManagedEntries = managedEntryCount > 1;

  const isSaveDisabled =
    isOverAllocationLimit || hasMultipleManagedEntries || !hasChanges || isSaving;

  const entryKeys = useMemo(
    () => new Set(editEntries.map((e) => `${e.entityType}-${e.productId}`)),
    [editEntries]
  );

  // Managed strategies lead the list: there are only a handful of them and an
  // admin searching for one should not have to page past the whole catalogue.
  const addableProducts = useMemo<AddableProduct[]>(
    () => [...managedItems, ...productItems],
    [managedItems, productItems]
  );

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return addableProducts
      .filter((p) => {
        const key = `${p.productType}-${p.productId}`;
        if (entryKeys.has(key)) return false;
        return (
          p.title.toLowerCase().includes(q) ||
          (p.providerName ?? "").toLowerCase().includes(q) ||
          String(p.productId).includes(q)
        );
      })
      .slice(0, 20);
  }, [searchQuery, addableProducts, entryKeys]);

  const handleWeightChange = useCallback(
    (entityType: string, productId: number, raw: string) => {
      const parsed = parseFloat(raw);
      const val = isNaN(parsed) || parsed < 0 ? 0 : parsed;
      setEditEntries((prev) =>
        prev.map((e) =>
          e.entityType === entityType && e.productId === productId
            ? { ...e, weight: val }
            : e
        )
      );
    },
    []
  );

  const handleRemove = useCallback(
    (entityType: string, productId: number) => {
      const key = `${entityType}-${productId}`;
      setDollarInputs((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setEditEntries((prev) =>
        prev.filter(
          (e) => !(e.entityType === entityType && e.productId === productId)
        )
      );
    },
    []
  );

  // A managed strategy has no provider, so it shows as a bare title. Kept in one
  // place so the dropdown row and the added row can never disagree.
  const addableLabel = (item: AddableProduct): string =>
    item.providerName ? `[${item.providerName}] ${item.title}` : item.title;

  const handleAddProduct = useCallback(
    (item: AddableProduct) => {
      const key = `${item.productType}-${item.productId}`;
      if (entryKeys.has(key)) return;
      setDollarInputs((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setEditEntries((prev) => [
        ...prev,
        {
          entityType: item.productType,
          productId: item.productId,
          weight: 0,
          position: "LONG",
          displayName: item.providerName
            ? `[${item.providerName}] ${item.title}`
            : item.title,
        },
      ]);
      setSearchQuery("");
      setShowDropdown(false);
    },
    [entryKeys]
  );

  const handleSave = useCallback(async () => {
    // Belt and braces: the Save button is disabled in this state, but a save
    // must never be the thing that puts the investor's own screen fail-closed.
    if (hasMultipleManagedEntries) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const payload = editEntries
        .filter((e) => e.weight > 0)
        .map((e) => ({
          entityType: e.entityType,
          productId: e.productId,
          weight: e.weight,
          position: "LONG" as const,
        }));
      // This editor renders every entity type it can express, managed strategy
      // included, so a row removed above is removed for real. Records outside
      // that set (a future entity type) are still preserved by the service.
      await investorStrategyService.syncRenderedStrategiesForUser(
        userId,
        payload,
        isAdminEditableRecord
      );
      onSave();
    } catch {
      setSaveError("Failed to save strategy. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [editEntries, hasMultipleManagedEntries, userId, onSave]);

  const preventScrollChange = (e: React.WheelEvent<HTMLInputElement>) => {
    (e.target as HTMLInputElement).blur();
  };

  const getEntryKey = (entry: Pick<EditEntry, "entityType" | "productId">): string =>
    `${entry.entityType}-${entry.productId}`;

  const formatDollarInputValue = (entry: EditEntry): string => {
    const key = getEntryKey(entry);
    if (dollarInputs[key] !== undefined) return dollarInputs[key];
    if (!canEditDollarAllocation || !isValidNlv(nlv) || entry.weight <= 0) return "";
    return String(weightToWholeDollars(entry.weight, nlv));
  };

  const clearDollarInput = (entry: Pick<EditEntry, "entityType" | "productId">) => {
    const key = getEntryKey(entry);
    setDollarInputs((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handlePercentChange = (entry: EditEntry, raw: string) => {
    clearDollarInput(entry);
    handleWeightChange(entry.entityType, entry.productId, raw);
  };

  const handleDollarChange = (entry: EditEntry, raw: string) => {
    if (!canEditDollarAllocation || !isValidNlv(nlv)) return;
    const key = getEntryKey(entry);
    setDollarInputs((prev) => ({ ...prev, [key]: raw }));
    const parsed = raw.trim() === "" ? 0 : parseFloat(raw);
    const weight = wholeDollarsToWeight(parsed, nlv);
    setEditEntries((prev) =>
      prev.map((e) =>
        e.entityType === entry.entityType && e.productId === entry.productId
          ? { ...e, weight }
          : e
      )
    );
  };

  const handleDollarBlur = (entry: EditEntry) => {
    clearDollarInput(entry);
  };

  const inputBaseClass = "bg-transparent dark:bg-rp-surface-raised border rounded py-1 text-sm font-mono text-right text-gray-900 dark:text-white appearance-none focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber";

  const renderPercentInput = (entry: EditEntry) => (
    <div className="relative inline-block w-20">
      <input
        type="number"
        min={0}
        max={allocationLimit}
        step={1}
        aria-label={`${entry.displayName} percent allocation`}
        value={entry.weight ? roundToDecimals(entry.weight) : ""}
        placeholder="0"
        onWheel={preventScrollChange}
        onChange={(e) => handlePercentChange(entry, e.target.value)}
        className={`w-full pl-2 pr-6 ${inputBaseClass} ${
          entry.weight === 0
            ? "border-rp-amber/50"
            : "border-gray-200 dark:border-rp-separator"
        }`}
      />
      <span aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs font-mono text-gray-500 dark:text-gray-400">
        %
      </span>
    </div>
  );

  const renderDollarInput = (entry: EditEntry) => (
    <div className="relative inline-block w-24">
      <input
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        aria-label={`${entry.displayName} dollar allocation`}
        value={formatDollarInputValue(entry)}
        placeholder="0"
        onWheel={preventScrollChange}
        onChange={(e) => handleDollarChange(entry, e.target.value)}
        onBlur={() => handleDollarBlur(entry)}
        className={`w-full pl-5 pr-2 ${inputBaseClass} ${
          entry.weight === 0
            ? "border-rp-amber/50"
            : "border-gray-200 dark:border-rp-separator"
        }`}
      />
      <span aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs font-mono text-gray-500 dark:text-gray-400">
        $
      </span>
    </div>
  );

  const renderAllocationCell = (entry: EditEntry) => (
    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white w-32">
      {canEditDollarAllocation ? renderDollarInput(entry) : "--"}
    </td>
  );

  const renderWeightCell = (entry: EditEntry) => (
    <td className="px-4 py-2.5 text-right w-28">
      {renderPercentInput(entry)}
    </td>
  );

  return (
    <div className="bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-rp-separator">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
          Edit Strategy
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            disabled={isSaveDisabled}
            onClick={handleSave}
            className="px-4 py-1.5 text-sm font-medium bg-rp-amber text-white rounded hover:bg-yellow-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save
          </button>
        </div>
      </div>

      <ScrollableTableWrapper>
        <table className="w-full min-w-[620px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-rp-separator">
              <th className="text-[11px] text-gray-500 uppercase tracking-wider text-right px-4 py-3 w-16">
                ID
              </th>
              <th className="text-[11px] text-gray-500 uppercase tracking-wider text-left px-4 py-3">
                Product
              </th>
              <th className="text-[11px] text-gray-500 uppercase tracking-wider text-left px-4 py-3 w-24">
                Type
              </th>
              <th className="text-[11px] text-gray-500 uppercase tracking-wider text-right px-4 py-3 w-32">
                Allocation ($)
              </th>
              <th className="text-[11px] text-gray-500 uppercase tracking-wider text-right px-4 py-3 w-28">
                Weight (%)
              </th>
              <th className="text-[11px] text-gray-500 uppercase tracking-wider text-center px-4 py-3 w-16" />
            </tr>
          </thead>
          <tbody>
            {editEntries.map((entry) => (
              <tr
                key={`${entry.entityType}-${entry.productId}`}
                className="border-b border-gray-100 dark:border-rp-separator last:border-b-0 hover:bg-gray-100 dark:hover:bg-white/[0.02] transition-colors"
              >
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-xs text-gray-400 dark:text-gray-500">
                  {entry.productId}
                </td>
                <td className="px-4 py-2.5">
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {entry.displayName}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-left">
                  {entry.entityType === "MANAGED_STRATEGY" ? (
                    <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
                      Managed
                    </span>
                  ) : (
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                      {entry.entityType}
                    </span>
                  )}
                </td>
                {renderAllocationCell(entry)}
                {renderWeightCell(entry)}
                <td className="px-4 py-2.5 text-center w-16">
                  <button
                    aria-label={`Remove ${entry.displayName}`}
                    onClick={() =>
                      handleRemove(entry.entityType, entry.productId)
                    }
                    className="text-gray-400 hover:text-red-400 transition-colors p-1 rounded hover:bg-red-600/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {editEntries.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500"
                >
                  No products in strategy. Add products below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </ScrollableTableWrapper>

      {/* Add product search */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-rp-separator relative">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search products to add..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(e.target.value.trim().length > 0);
            }}
            onFocus={() => {
              if (searchQuery.trim()) setShowDropdown(true);
            }}
            className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setShowDropdown(false);
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {showDropdown && filteredProducts.length > 0 && (
          <div className="absolute left-0 right-0 bottom-full mb-1 mx-4 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg shadow-lg max-h-60 overflow-y-auto z-20">
            {filteredProducts.map((item) => (
              <button
                key={`${item.productType}-${item.productId}`}
                onClick={() => handleAddProduct(item)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-colors flex items-center justify-between"
              >
                <span className="text-gray-900 dark:text-white truncate">
                  {addableLabel(item)}
                </span>
                <span className="text-xs font-mono text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">
                  {item.productType} #{item.productId}
                </span>
              </button>
            ))}
          </div>
        )}
        {showDropdown && searchQuery.trim() && filteredProducts.length === 0 && (
          <div className="absolute left-0 right-0 bottom-full mb-1 mx-4 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg shadow-lg z-20">
            <div className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500">
              No matching products found
            </div>
          </div>
        )}
      </div>

      {/* Running total + validation */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-rp-separator flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span
            className={`text-sm font-mono font-medium ${
              !isOverAllocationLimit ? "text-teal-400" : "text-red-400"
            }`}
          >
            Total: {formatWeight(totalWeight)}% / {formatWeight(editableLimit)}%
          </span>
          {hasMultipleManagedEntries && (
            <span className="text-red-400 text-xs" data-testid="admin-strategy-multi-managed-note">
              Only one managed strategy can be held at a time -- remove one to save
            </span>
          )}
          {isOverAllocationLimit && (
            <span className="text-red-400 text-xs">
              Exceeds {formatWeight(editableLimit)}% -- reduce weights to save
            </span>
          )}
        </div>
        {saveError && (
          <span className="text-red-500 text-xs">{saveError}</span>
        )}
      </div>
    </div>
  );
};

export default AdminStrategyEditor;
